/**
 * DocLink consult type enum (values sent in requests / returned from APIs).
 * Colours are fixed per type so HubMed pie + stacked charts stay consistent.
 */

export const DocLinkConsultTypes = {
  NONE: 0,
  SCRIPT_REQUEST: 1,
  SHORT_CONSULT: 2,
  MULTI_SCRIPT_REQUEST: 3,
  LONG_CONSULT: 4,
  MEDCERTS_WORK: 5,
  MEDCERTS_CARER: 6,
  MEDCERTS_UNIV: 7,
  WEIGHT_LOSS: 8,
  FOLLOWUP_WEIGHTLOSS: 9,
  BULK_BILLED: 10,
  COVID_ANTIVIRAL: 11,
  ADHD_INITIAL: 12,
  SMOKING: 13,
  CANNABIS_INITIAL: 14,
  CANNABIS_REPEAT: 15,
  MENOPAUSE: 16,
  SEXUAL_HEALTH: 17,
  ADHD_REPEAT: 18,
  SPECIALIST_REFERRAL: 19,
};

/** Human-readable labels (aligned with legacy server naming where applicable). */
export const DocLinkConsultTypeLabels = {
  [DocLinkConsultTypes.NONE]: "None",
  [DocLinkConsultTypes.SCRIPT_REQUEST]: "Script Request",
  [DocLinkConsultTypes.SHORT_CONSULT]: "Short Consult",
  [DocLinkConsultTypes.MULTI_SCRIPT_REQUEST]: "Multi Script Request",
  [DocLinkConsultTypes.LONG_CONSULT]: "Long Consult",
  [DocLinkConsultTypes.MEDCERTS_WORK]: "MedCerts Work",
  [DocLinkConsultTypes.MEDCERTS_CARER]: "MedCerts Carer",
  [DocLinkConsultTypes.MEDCERTS_UNIV]: "MedCerts University",
  [DocLinkConsultTypes.WEIGHT_LOSS]: "Weight Loss",
  [DocLinkConsultTypes.FOLLOWUP_WEIGHTLOSS]: "Follow-up Weight Loss",
  [DocLinkConsultTypes.BULK_BILLED]: "Bulk Billed",
  [DocLinkConsultTypes.COVID_ANTIVIRAL]: "COVID Antiviral",
  [DocLinkConsultTypes.ADHD_INITIAL]: "ADHD Initial",
  [DocLinkConsultTypes.SMOKING]: "Smoking",
  [DocLinkConsultTypes.CANNABIS_INITIAL]: "Cannabis Initial",
  [DocLinkConsultTypes.CANNABIS_REPEAT]: "Cannabis Repeat",
  [DocLinkConsultTypes.MENOPAUSE]: "Menopause",
  [DocLinkConsultTypes.SEXUAL_HEALTH]: "Sexual Health",
  [DocLinkConsultTypes.ADHD_REPEAT]: "ADHD Repeat",
  [DocLinkConsultTypes.SPECIALIST_REFERRAL]: "Specialist Referral",
};

/**
 * Distinct hex per enum — spaced across hue/lightness to limit confusion
 * (no two adjacent greens; ADHD initial vs repeat differ strongly, etc.).
 */
export const DocLinkConsultTypeColors = {
  [DocLinkConsultTypes.NONE]: "#94a3b8",
  [DocLinkConsultTypes.SCRIPT_REQUEST]: "#1d4ed8",
  [DocLinkConsultTypes.SHORT_CONSULT]: "#6366f1",
  [DocLinkConsultTypes.MULTI_SCRIPT_REQUEST]: "#0e7490",
  [DocLinkConsultTypes.LONG_CONSULT]: "#5b21b6",
  [DocLinkConsultTypes.MEDCERTS_WORK]: "#be185d",
  [DocLinkConsultTypes.MEDCERTS_CARER]: "#c026d3",
  [DocLinkConsultTypes.MEDCERTS_UNIV]: "#6b21a8",
  [DocLinkConsultTypes.WEIGHT_LOSS]: "#b45309",
  [DocLinkConsultTypes.FOLLOWUP_WEIGHTLOSS]: "#ea580c",
  [DocLinkConsultTypes.BULK_BILLED]: "#0d9488",
  [DocLinkConsultTypes.COVID_ANTIVIRAL]: "#0284c7",
  [DocLinkConsultTypes.ADHD_INITIAL]: "#2563eb",
  [DocLinkConsultTypes.SMOKING]: "#57534e",
  [DocLinkConsultTypes.CANNABIS_INITIAL]: "#166534",
  [DocLinkConsultTypes.CANNABIS_REPEAT]: "#65a30d",
  [DocLinkConsultTypes.MENOPAUSE]: "#9f1239",
  [DocLinkConsultTypes.SEXUAL_HEALTH]: "#db2777",
  [DocLinkConsultTypes.ADHD_REPEAT]: "#f59e0b",
  [DocLinkConsultTypes.SPECIALIST_REFERRAL]: "#0f172a",
};

const UNKNOWN_COLOR = "#64748b";

/** HubMed overview stacked buckets → representative consult type for colour. */
export const HubMedOverviewBucketToConsultType = {
  "Script request": DocLinkConsultTypes.SCRIPT_REQUEST,
  "Weight loss": DocLinkConsultTypes.WEIGHT_LOSS,
  "Follow up weight loss": DocLinkConsultTypes.FOLLOWUP_WEIGHTLOSS,
  "Bulk billed": DocLinkConsultTypes.BULK_BILLED,
  ADHD: DocLinkConsultTypes.ADHD_INITIAL,
  Smoking: DocLinkConsultTypes.SMOKING,
  Cannabis: DocLinkConsultTypes.CANNABIS_INITIAL,
  Menopause: DocLinkConsultTypes.MENOPAUSE,
  "Sexual health": DocLinkConsultTypes.SEXUAL_HEALTH,
  Specialist: DocLinkConsultTypes.SPECIALIST_REFERRAL,
};

export function getDocLinkConsultTypeLabel(type) {
  const t = Number(type);
  if (!Number.isFinite(t)) return "Unknown Consult Type";
  return DocLinkConsultTypeLabels[t] ?? "Unknown Consult Type";
}

export function getDocLinkConsultTypeColor(type) {
  const t = Number(type);
  if (!Number.isFinite(t)) return UNKNOWN_COLOR;
  return DocLinkConsultTypeColors[t] ?? UNKNOWN_COLOR;
}

export function getHubMedOverviewBucketColor(bucketKey) {
  const code = HubMedOverviewBucketToConsultType[bucketKey];
  if (code == null) return UNKNOWN_COLOR;
  return getDocLinkConsultTypeColor(code);
}
